/**
 * @file services/models.ts
 * @description Service for handling models (fulfillments) data for Model Reviewer/Preparer Dashboard
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterTypeSelection , MODEL_FILTER_TYPES } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { clientWithAuth } from 'dashboard409a/services/base';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';

// Query keys for models operations
export const ModelsQueryKeys = {
  MODELS_LIST: 'models-list',
} as const;

// Convert FilterTypeSelection checkboxes to the status_filters list the backend expects
export const filterTypeSelectionToStatusFilters = (selection: FilterTypeSelection): string[] => {
  const filters: string[] = [];
  if (selection.assigned) {
    filters.push(MODEL_FILTER_TYPES.ASSIGNED);
  }
  if (selection.unassigned) {
    filters.push(MODEL_FILTER_TYPES.UNASSIGNED);
  }
  return filters;
};

interface ModelsResponse {
  count: number;
  total_pages: number;
  page: number;
  page_size: number;
  results: ModelWithValuationData[];
}

export interface UseModelsPaginatedParams {
  filterTypeSelection: FilterTypeSelection;
  preparerUserIds?: number[];
  reviewerUserIds?: number[];
  fromDate?: string;
  toDate?: string;
  companyName?: string;
  modelStatuses?: number[];
}

export interface UseModelsPaginatedReturn {
  data: ModelWithValuationData[];
  isLoading: boolean;
  isLoadingMore: boolean;
  totalCount: number;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook that loads ALL models from the backend in a single request.
 * No `page` field is sent → backend returns every record that matches the filters.
 *
 * After the DB records are in hand the caller (useModelsWithValuationData) enriches
 * them with Attio data progressively, batch by batch.
 */
export const useModelsAll = ({
  filterTypeSelection,
  preparerUserIds,
  reviewerUserIds,
  fromDate,
  toDate,
  companyName,
  modelStatuses,
}: UseModelsPaginatedParams): UseModelsPaginatedReturn => {
  const [data, setData] = useState<ModelWithValuationData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => setRefreshCount(c => c + 1), []);

  // Stable key so that the effect only re-fires when filters actually change
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        filterTypeSelection,
        preparerUserIds: [...(preparerUserIds ?? [])].sort((a, b) => a - b),
        reviewerUserIds: [...(reviewerUserIds ?? [])].sort((a, b) => a - b),
        fromDate: fromDate ?? null,
        toDate: toDate ?? null,
        companyName: companyName ?? null,
        modelStatuses: [...(modelStatuses ?? [])].sort((a, b) => a - b),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterTypeSelection, preparerUserIds, reviewerUserIds, fromDate, toDate, companyName, modelStatuses]
  );

  useEffect(() => {
    // Cancel any in-progress fetch from a previous filter state
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    const statusFilters = filterTypeSelectionToStatusFilters(filterTypeSelection);

    // If nothing is selected, return empty data immediately without making a request
    if (statusFilters.length === 0) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      setData([]);
      setTotalCount(0);
      setError(null);

      try {
        // No `page` field → backend returns all matching records in one shot
        const response = await clientWithAuth.post<ModelsResponse>(
          '/api/dashboard/user/models/',
          {
            preparer_ids: preparerUserIds ?? [],
            reviewer_ids: reviewerUserIds ?? [],
            from_date: fromDate ?? null,
            to_date: toDate ?? null,
            company: companyName ?? null,
            status_filters: statusFilters,
            model_statuses: modelStatuses ?? [],
          },
          { signal }
        );

        if (signal.aborted) {
          return;
        }

        setData(response.data.results);
        setTotalCount(response.data.count);
      } catch (err) {
        if (!signal.aborted) {
          // eslint-disable-next-line no-console
          console.error('Error loading models:', err);
          setError(err instanceof Error ? err : new Error('Failed to load models'));
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      abortController.abort();
    };
    // filtersKey and refreshCount are the deps — refreshCount allows forcing a refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, refreshCount]);

  return { data, isLoading, isLoadingMore: false, totalCount, error, refetch };
};
